import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useSelector } from "react-redux";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DownloadIcon from "@mui/icons-material/Download";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";

const API_URL = "https://india.tnibro.com/tnibroadmin/conversion-analytics/";
const PAID_USERS_URL = "/tnibroadmin/export/usercountsforcustom";
const AUTOPAY_URL = "/payment/datewise-autopayment/";

const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const textMain = "#111827";
const textMuted = "#6b7280";
const accentGreen = "#0f9d6b";

const todayStr = () => new Date().toISOString().slice(0, 10);

const toNum = (n) => {
  if (typeof n === "number" && !isNaN(n)) return n;
  if (typeof n === "string" && n.trim() !== "" && !isNaN(Number(n))) return Number(n);
  return null;
};
const fmtPct = (num, den) => {
  const n = toNum(num);
  const d = toNum(den);
  return n === null || !d ? "—" : `${((n / d) * 100).toFixed(1)}%`;
};
const AUTOPAY_METRICS = [
  ["Expected", "expected_count", "expected_amount", "#1d4ed8", "#eff6ff", "#bfdbfe"],
  ["Paid", "count", "total_amount", accentGreen, "#f0fdf8", "#bbf0dc"],
  ["Pending", "pending_count", "pending_amount", "#b45309", "#fffbeb", "#fde68a"],
];
const AUTOPAY_COLS = ["Date", "Expected (Count / Amount)", "Paid (Count / Amount)", "Pending (Count / Amount)", "Conversion %"];
const fmtNum = (n) => {
  const v = toNum(n);
  return v === null ? "—" : v.toLocaleString("en-IN");
};
const fmtCurrency = (n) => {
  const v = toNum(n);
  return v === null ? "—" : `₹${v.toLocaleString("en-IN")}`;
};

const StatCard = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 180,
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: "12px",
      p: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Typography sx={{ fontSize: 13, color: textMuted, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 700, color: textMain }}>{value}</Typography>
  </Box>
);

const dateFieldSx = { background: "#fff", borderRadius: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } };
const dateLabelSx = { color: textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" };

const DateRangeFilter = ({ start, end, onStart, onEnd, onApply }) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography sx={dateLabelSx}>Start Date *</Typography>
      <TextField type="date" size="small" required value={start} onChange={(e) => onStart(e.target.value)} sx={dateFieldSx} />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography sx={dateLabelSx}>End Date</Typography>
      <TextField type="date" size="small" value={end} onChange={(e) => onEnd(e.target.value)} sx={dateFieldSx} />
    </Box>
    <Button
      variant="contained"
      onClick={onApply}
      sx={{
        background: "#016551", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 2.5,
        "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
      }}
    >
      Apply
    </Button>
  </Box>
);

const COLUMNS = [
  { key: "date", label: "date" },
  { key: "day", label: "day" },
  { key: "d0_signup", label: "d0_signup" },
  { key: "d0_conversion", label: "d0_conversion" },
  { key: "d0_revenue", label: "d0_revenue" },
  { key: "m0_conversion", label: "m0_conversion" },
  { key: "m0_revenue", label: "m0_revenue" },
  { key: "overall_conversion", label: "overall_conversion" },
  { key: "overall_revenue", label: "overall_revenue" },
  { key: "total_payment_count", label: "total_payment_count" },
  { key: "total_payment_amount", label: "total_payment_amount" },
];

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const prettyKey = (k) => String(k).replace(/_/g, " ");
const fmtCell = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  const n = toNum(v);
  return n !== null && typeof v === "number" ? n.toLocaleString("en-IN") : String(v);
};

// {"1998.0":5,"1998":1,"421.00":71} -> [{amount:421,count:71}, {amount:1998,count:6}] sorted by amount
const groupByAmount = (obj) => {
  const map = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    const amt = Number(k);
    const key = isNaN(amt) ? k : amt;
    map[key] = (map[key] || 0) + (toNum(v) || 0);
  });
  return Object.entries(map)
    .map(([amount, count]) => ({ amount: isNaN(Number(amount)) ? amount : Number(amount), count }))
    .sort((a, b) => (typeof a.amount === "number" && typeof b.amount === "number" ? a.amount - b.amount : 0));
};

const AmountChips = ({ data }) => {
  const items = groupByAmount(data);
  if (items.length === 0) return <span style={{ color: textMuted }}>—</span>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", py: "2px" }}>
      {items.map(({ amount, count }) => (
        <Box
          key={amount}
          sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: "#f0fdf8", border: "1px solid #bbf0dc", borderRadius: "999px",
            px: "10px", py: "3px", fontSize: 12.5, whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: textMain }}>
            {typeof amount === "number" ? fmtCurrency(amount) : amount}
          </Box>
          <Box component="span" sx={{ color: "#fff", background: accentGreen, borderRadius: "999px", px: "7px", fontWeight: 600, fontSize: 11.5 }}>
            ×{fmtNum(count)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const fmtDate = (v) => (/^\d{4}[/-]\d{2}[/-]\d{2}$/.test(String(v)) ? String(v).split(/[/-]/).reverse().join("/") : v);

const renderPaidCell = (key, v) => {
  if (isPlainObject(v)) return <AmountChips data={v} />;
  if (key === "date") return fmtDate(v);
  return fmtCell(v);
};

// Normalises the API payload into { list, summary }: list = array of row objects, summary = scalar key/values
const normalizePaidUsers = (payload) => {
  const body = isPlainObject(payload) && payload.data !== undefined ? payload.data : payload;
  if (Array.isArray(body)) return { list: body.filter(isPlainObject), summary: {} };
  if (isPlainObject(body)) {
    const summary = {};
    let list = [];
    Object.entries(body).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length && isPlainObject(v[0])) list = v;
      else if (!isPlainObject(v) && !Array.isArray(v)) summary[k] = v;
    });
    return { list, summary };
  }
  return { list: [], summary: {} };
};

const ConversionAnalytics = () => {
  const userState = useSelector((store) => store.auth);
  const userEmail = userState?.user?.userDetails?.email;

  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [paidStart, setPaidStart] = useState(todayStr());
  const [paidEnd, setPaidEnd] = useState(todayStr());
  const [autoStart, setAutoStart] = useState(todayStr());
  const [autoEnd, setAutoEnd] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [rows, setRows] = useState([]);
  const [autopayLoading, setAutopayLoading] = useState(true);
  const [autopayRows, setAutopayRows] = useState([]);
  const [paidDownloading, setPaidDownloading] = useState(false);
  const [paidLoading, setPaidLoading] = useState(true);
  const [paidUsers, setPaidUsers] = useState({ list: [], summary: {} });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState("");

  const fetchData = async (start, end) => {
    if (!start) {
      toast.error("Start date is required");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: API_URL,
        params: {
          start_date: start,
          ...(end ? { end_date: end } : {}),
        },
      });
      const data = res.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("conversion-analytics error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load conversion analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaidUsers = async (start, end) => {
    if (!start) return;
    setPaidLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: PAID_USERS_URL,
        params: {
          start_date: start,
          end_date: end || start,
          filter_type: "all_paid_users",
        },
      });
      setPaidUsers(normalizePaidUsers(res.data));
    } catch (error) {
      console.log("paid users error: ", error);
      setPaidUsers({ list: [], summary: {} });
      toast.error(error?.response?.data?.response || "Failed to load paid users data");
    } finally {
      setPaidLoading(false);
    }
  };

  // Backend is paginated and returns all dates, so walk the pages and keep only the selected range
  const fetchAutopay = async (start, end) => {
    if (!start) return;
    const to = end || start;
    setAutopayLoading(true);
    try {
      const collected = [];
      let page = 1;
      let hasNext = true;
      while (hasNext && page <= 30) {
        const res = await axiosConfig({
          method: "get",
          url: AUTOPAY_URL,
          params: { ...(page > 1 ? { page } : {}), start_date: start, end_date: to },
        });
        const body = res.data?.results?.data || res.data?.results || res.data?.data || [];
        collected.push(...(Array.isArray(body) ? body : []));
        hasNext = Boolean(res.data?.next);
        page += 1;
      }
      const inRange = collected
        .filter((r) => {
          const d = String(r.date || "").replace(/\//g, "-").slice(0, 10);
          return d >= start && d <= to;
        })
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
      setAutopayRows(inRange);
    } catch (error) {
      console.log("autopay datewise error: ", error);
      setAutopayRows([]);
      toast.error(error?.response?.data?.detail || "Failed to load autopay data");
    } finally {
      setAutopayLoading(false);
    }
  };

  useEffect(() => {
    fetchData(startDate, endDate);
    fetchPaidUsers(paidStart, paidEnd);
    fetchAutopay(autoStart, autoEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The endpoint only supports export_type=csv (same as the User Counts page); opens fine in Excel
  const handleDownloadPaidUsers = async () => {
    if (!paidStart) {
      toast.error("Start date is required");
      return;
    }
    const to = paidEnd || paidStart;
    setPaidDownloading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: PAID_USERS_URL,
        params: { start_date: paidStart, end_date: to, filter_type: "all_paid_users", export_type: "csv" },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `all-paid-users_${paidStart}_to_${to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("File downloaded");
    } catch (error) {
      console.log("paid users download error: ", error);
      toast.error("Failed to download file");
    } finally {
      setPaidDownloading(false);
    }
  };

  const handleApply = () => fetchData(startDate, endDate);
  const handleApplyPaid = () => fetchPaidUsers(paidStart, paidEnd);
  const handleApplyAutopay = () => fetchAutopay(autoStart, autoEnd);

  const handleOpenEmailDialog = () => {
    if (!startDate) {
      toast.error("Start date is required");
      return;
    }
    setDownloadEmail(userEmail || "");
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => setEmailDialogOpen(false);

  const handleDownload = async () => {
    if (!downloadEmail.trim()) {
      toast.error("Please enter an email");
      return;
    }
    setDownloading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: API_URL,
        params: {
          start_date: startDate,
          ...(endDate ? { end_date: endDate } : {}),
          download: true,
          email: downloadEmail.trim(),
        },
        responseType: "blob",
      });

      const contentType = res.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        toast.success(
          typeof parsed?.response === "string" ? parsed.response : "Excel will be sent to your email"
        );
        handleCloseEmailDialog();
        return;
      }

      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `conversion-analytics_${startDate}_to_${endDate || startDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Excel downloaded");
      handleCloseEmailDialog();
    } catch (error) {
      console.log("conversion-analytics download error: ", error);
      toast.error("Failed to download excel");
    } finally {
      setDownloading(false);
    }
  };

  const stats = useMemo(() => ({
    totalSignups: rows.reduce((sum, r) => sum + (toNum(r.d0_signup) || 0), 0),
    totalConversions: rows.reduce((sum, r) => sum + (toNum(r.d0_conversion) || 0), 0),
    totalRevenue: rows.reduce((sum, r) => sum + (toNum(r.overall_revenue) || 0), 0),
    totalPayments: rows.reduce((sum, r) => sum + (toNum(r.total_payment_count) || 0), 0),
  }), [rows]);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TrendingUpIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Conversion Analytics
            </Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              Track how notifications and engagement drive conversions
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleOpenEmailDialog}
            sx={{
              borderColor: "#eef2f7", color: "#eef2f7", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 2.5,
              "&:hover": { background: "rgba(255,255,255,0.1)", borderColor: "#eef2f7" },
            }}
          >
            Download Excel
          </Button>
        </Box>
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Total D0 Signups" value={fmtNum(stats.totalSignups)} />
        <StatCard label="Total D0 Conversions" value={fmtNum(stats.totalConversions)} />
        <StatCard label="Total Revenue" value={fmtCurrency(stats.totalRevenue)} />
        <StatCard label="Total Payments Count" value={fmtNum(stats.totalPayments)} />
      </Box>

      {/* Table */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: textMain }}>
            Daily Breakdown ({startDate} to {endDate || startDate})
          </Typography>
          <DateRangeFilter start={startDate} end={endDate} onStart={setStartDate} onEnd={setEndDate} onApply={handleApply} />
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {COLUMNS.map((c) => (
                  <Box
                    component="th"
                    key={c.key}
                    sx={{
                      textAlign: "left", fontSize: 11.5, color: textMuted, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.3px",
                      p: "10px 16px", whiteSpace: "nowrap", borderBottom: `1px solid ${cardBorder}`,
                    }}
                  >
                    {c.label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={COLUMNS.length} sx={{ p: 5, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: accentGreen }} />
                  </Box>
                </Box>
              ) : rows.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={COLUMNS.length} sx={{ p: 5, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No data found for this range
                  </Box>
                </Box>
              ) : (
                rows.map((r, idx) => (
                  <Box component="tr" key={`${r.date}-${idx}`}>
                    {COLUMNS.map((c) => (
                      <Box
                        component="td"
                        key={c.key}
                        sx={{ p: "10px 16px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}
                      >
                        {c.key.includes("revenue") || c.key.includes("amount")
                          ? fmtCurrency(r[c.key])
                          : c.key === "date" || c.key === "day"
                            ? r[c.key]
                            : fmtNum(r[c.key])}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* All paid users (date filtered) */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", overflow: "hidden", mt: 3 }}>
        <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: textMain }}>
            All Paid Users ({paidStart} to {paidEnd || paidStart})
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          <DateRangeFilter start={paidStart} end={paidEnd} onStart={setPaidStart} onEnd={setPaidEnd} onApply={handleApplyPaid} />
          <Button
            variant="outlined"
            size="small"
            startIcon={paidDownloading ? <CircularProgress size={14} sx={{ color: accentGreen }} /> : <DownloadIcon />}
            onClick={handleDownloadPaidUsers}
            disabled={paidDownloading}
            sx={{
              borderColor: accentGreen, color: accentGreen, borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 2,
              "&:hover": { background: "#f0fdf8", borderColor: accentGreen },
            }}
          >
            Download Excel
          </Button>
          </Box>
        </Box>

        {paidLoading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress size={26} sx={{ color: accentGreen }} />
          </Box>
        ) : paidUsers.list.length === 0 && Object.keys(paidUsers.summary).length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center", fontSize: 13, color: textMuted }}>
            No data found for this range
          </Box>
        ) : (
          <>
            {Object.keys(paidUsers.summary).length > 0 && (
              <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
                {Object.entries(paidUsers.summary).map(([k, v]) => (
                  <StatCard key={k} label={prettyKey(k)} value={fmtCell(v)} />
                ))}
              </Box>
            )}
            {paidUsers.list.length > 0 && (() => {
              const cols = Object.keys(paidUsers.list[0]);
              return (
                <Box sx={{ overflowX: "auto" }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                    <Box component="thead">
                      <Box component="tr">
                        {cols.map((c) => (
                          <Box
                            component="th"
                            key={c}
                            sx={{
                              textAlign: "left", fontSize: 11.5, color: textMuted, fontWeight: 600,
                              textTransform: "uppercase", letterSpacing: "0.3px",
                              p: "10px 16px", whiteSpace: "nowrap", borderBottom: `1px solid ${cardBorder}`,
                            }}
                          >
                            {prettyKey(c)}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {paidUsers.list.map((r, idx) => (
                        <Box component="tr" key={idx}>
                          {cols.map((c) => (
                            <Box
                              component="td"
                              key={c}
                              sx={{ p: "10px 16px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: isPlainObject(r[c]) ? "normal" : "nowrap", verticalAlign: "middle" }}
                            >
                              {renderPaidCell(c, r[c])}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* Date-wise autopayments */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "16px", overflow: "hidden", mt: 3, boxShadow: "0 4px 20px rgba(15,23,42,0.06)" }}>
        <Box sx={{ p: "18px 24px", background: "linear-gradient(135deg, #f0fdf8 0%, #ffffff 70%)", borderBottom: `1px solid ${cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: textMain }}>Autopayments Date-wise</Typography>
            <Typography sx={{ fontSize: 12.5, color: textMuted, mt: 0.25 }}>
              {autoStart} to {autoEnd || autoStart}
            </Typography>
          </Box>
          <DateRangeFilter start={autoStart} end={autoEnd} onStart={setAutoStart} onEnd={setAutoEnd} onApply={handleApplyAutopay} />
        </Box>

        {!autopayLoading && autopayRows.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", p: "16px 24px", borderBottom: `1px solid ${cardBorder}` }}>
            {AUTOPAY_METRICS.map(([label, cKey, aKey, color, bg, border]) => {
              const sum = (k) => autopayRows.reduce((t, r) => t + (toNum(r[k]) || 0), 0);
              return (
                <Box key={cKey} sx={{ flex: 1, minWidth: 160, background: bg, border: `1px solid ${border}`, borderRadius: "12px", p: "12px 16px" }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: textMain, lineHeight: 1.3 }}>{fmtNum(sum(cKey))}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: textMuted }}>{fmtCurrency(sum(aKey))}</Typography>
                </Box>
              );
            })}
            <Box sx={{ flex: 1, minWidth: 160, background: "#111827", borderRadius: "12px", p: "12px 16px" }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#6ee7b7", textTransform: "uppercase", letterSpacing: "0.5px" }}>Conversion</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
                {fmtPct(
                  autopayRows.reduce((t, r) => t + (toNum(r.count) || 0), 0),
                  autopayRows.reduce((t, r) => t + (toNum(r.expected_count) || 0), 0),
                )}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#9ca3af" }}>Paid / Expected</Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr" sx={{ background: "#f8fafc" }}>
                {AUTOPAY_COLS.map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left", fontSize: 11.5, color: textMuted, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.5px",
                      p: "12px 20px", whiteSpace: "nowrap", borderBottom: `1px solid ${cardBorder}`,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {autopayLoading ? (
                <Box component="tr">
                  <Box component="td" colSpan={AUTOPAY_COLS.length} sx={{ p: 5, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: accentGreen }} />
                  </Box>
                </Box>
              ) : autopayRows.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={AUTOPAY_COLS.length} sx={{ p: 5, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No data found for this range
                  </Box>
                </Box>
              ) : (
                autopayRows.map((r, idx) => {
                  const pct = toNum(r.expected_count) ? Math.min(100, ((toNum(r.count) || 0) / toNum(r.expected_count)) * 100) : 0;
                  return (
                    <Box component="tr" key={`${r.date}-${idx}`} sx={{ "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}>
                      <Box component="td" sx={{ p: "14px 20px", fontSize: 13.5, fontWeight: 600, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                        {fmtDate(r.date)}
                      </Box>
                      {AUTOPAY_METRICS.map(([, cKey, aKey, color, bg, border]) => (
                        <Box component="td" key={cKey} sx={{ p: "14px 20px", fontSize: 13, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                          <Box component="span" sx={{ display: "inline-block", minWidth: 36, textAlign: "center", background: bg, border: `1px solid ${border}`, color, fontWeight: 700, borderRadius: "999px", px: "10px", py: "2px" }}>
                            {fmtNum(r[cKey])}
                          </Box>
                          <Box component="span" sx={{ ml: 1.25, color: textMuted, fontWeight: 500 }}>
                            {fmtCurrency(r[aKey])}
                          </Box>
                        </Box>
                      ))}
                      <Box component="td" sx={{ p: "14px 20px", borderBottom: `1px solid ${cardBorder}`, minWidth: 190 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: pct >= 50 ? accentGreen : pct >= 25 ? "#b45309" : "#dc2626" }}>
                            {fmtPct(r.count, r.expected_count)}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: textMuted }}>
                            ({fmtPct(r.total_amount, r.expected_amount)} amt)
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 0.75, height: 6, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
                          <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg, #34d399, #0f9d6b)" }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Email dialog for excel download */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: textMain }}>Download Excel</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="dense"
            value={downloadEmail}
            onChange={(e) => setDownloadEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEmailDialog} sx={{ color: textMuted, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={downloading || !downloadEmail.trim()}
            sx={{
              background: "#016551", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&:disabled": { background: "#9ca3af" },
            }}
          >
            {downloading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Download"}
          </Button>
        </DialogActions>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default ConversionAnalytics;
